import { Form, Input, Radio } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomInput,
  CustomModal,
  CustomSelect,
} from "sipli-fleet-core/components";
import {
  useMutationCreateBusinessActivity,
  useMutationUpdateBusinessActivity,
  useQueryGetSystemTypes,
} from "src/queries/administrationSetup.queries";
import type {
  GeneralSetupPayload,
  GeneralSetupRecord,
  NamedReference,
} from "src/services/administrationSetupService";
import Notification from "src/util/libs/notification";
import { isActive } from "../setupUtils";

interface BusinessActivitiesAddEditProps {
  open: boolean;
  record?: GeneralSetupRecord | null;
  onClose: () => void;
}
interface BusinessActivitiesFormValues {
  name: string;
  code: string;
  systemTypeId?: string | number;
  active: boolean;
  description?: string;
}
const BusinessActivitiesAddEdit = ({ open, record, onClose }: BusinessActivitiesAddEditProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<BusinessActivitiesFormValues>();
  const createMutation = useMutationCreateBusinessActivity();
  const updateMutation = useMutationUpdateBusinessActivity();
  const systemTypesQuery = useQueryGetSystemTypes(open);
  const systemTypeOptions = useMemo(() => {
    const response = systemTypesQuery.data?.data as {
      response?: { systemTypes?: NamedReference[] } | NamedReference[];
    };
    const values = Array.isArray(response?.response)
      ? response.response
      : (response?.response?.systemTypes ?? []);
    return values.map((item) => ({
      value: item.id!,
      label: item.name ?? item.code ?? String(item.id),
    }));
  }, [systemTypesQuery.data?.data]);
  useEffect(() => {
    if (open)
      form.setFieldsValue({
        name: record?.name ?? "",
        code: record?.code ?? "",
        systemTypeId: record?.systemType?.id,
        active: record ? isActive(record.active) : true,
        description: record?.description ?? "",
      });
  }, [form, open, record]);
  const handleClose = () => {
    form.resetFields();
    onClose();
  };
  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: GeneralSetupPayload = {
      name: values.name,
      code: values.code,
      systemTypeId: values.systemTypeId,
      active: values.active,
      description: values.description ?? "",
    };
    try {
      if (record) await updateMutation.mutateAsync({ id: record.id, payload });
      else await createMutation.mutateAsync(payload);
      Notification.success({
        message: record ? t("update_business_activity") : t("add_business_activity"),
      });
      handleClose();
    } catch (error: unknown) {
      const responseMessage =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      if (!responseMessage)
        Notification.error({ message: t("error"), description: t("setup_save_failed") });
    }
  };
  return (
    <CustomModal
      open={open}
      title={`${record ? t("edit") : t("add")} ${t("type_of_business")}`}
      onCancel={handleClose}
      destroyOnHidden
      width={720}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 pt-4 gap-x-4 sm:grid-cols-2"
      >
        <Form.Item
          name="code"
          label={t("business_activity_code")}
          rules={[
            { required: true, message: t("code_required") },
            { max: 12, message: "Code is too long. Maximum length is 12 characters." },
          ]}
          className="mb-3!"
        >
          <CustomInput maxLength={12} />
        </Form.Item>
        <Form.Item
          name="name"
          label={t("business_activity_name")}
          rules={[{ required: true, message: t("name_required") }]}
          className="mb-3!"
        >
          <CustomInput />
        </Form.Item>
        <Form.Item name="systemTypeId" label={t("system_type")} className="mb-3!">
          <CustomSelect
            options={systemTypeOptions}
            loading={systemTypesQuery.isLoading}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item name="active" label={t("status")} className="mb-3!">
          <Radio.Group optionType="button" buttonStyle="solid">
            <Radio.Button value>{t("active")}</Radio.Button>
            <Radio.Button value={false}>{t("inactive")}</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="description" label={t("description")} className="mb-3! sm:col-span-2">
          <Input.TextArea rows={4} />
        </Form.Item>
        <div className="sm:col-span-2">
          <CustomActionButtons
            onCancel={handleClose}
            onOk={handleSubmit}
            okBtnLabel={record ? "update" : "add"}
            loading={createMutation.isPending || updateMutation.isPending}
          />
        </div>
      </Form>
    </CustomModal>
  );
};
export default BusinessActivitiesAddEdit;
